import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, later, scheduleOnce } from '@ember/runloop';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BoardWallComponent from 'webapp/components/board/wall';
import LayoutDesignerBoardSettingsComponent from 'webapp/components/layout-designer/board-settings';
import type DesignerController from 'webapp/controllers/designer';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type BoardState from 'webapp/models-custom/board-state';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';

interface Args {
	board: Board;
	boardState: BoardState;
	designerController: DesignerController;
	onSave: () => void;
	onDelete: () => void;
	onCopy: () => void;
	onDatawallGridChange: (datawallGrid: DatawallGrid | null) => void;
	onLoopChange: (boardLoop: BoardLoop) => void;
}

type ResizeFn = (e: { target?: EventTarget | null }) => void;

export default class DesignerBoardWallComponent extends Component<Args> {
	topbarHeight = 60; // designer topbar
	boardSettingsHeight = 60; // board editor line above the preview
	openLeftSidebarWidth = 336; // w/o toggler (@designer_left_panel_width)
	openRightSidebarWidth = 375; // w/o toggler (@designer_right_panel_width)
	leftSidebarTogglerWidth = 56; // the toggler (@designer_toggler_size)
	rightSidebarOverflow = 16; // the sort handle (@designer_right_panel_overflow)
	loopBoxHeight = 142; // height of expanded loop box (120) + some margin (22)
	minMargin = 12; // minimal margin for top, left, right positioning
	boardPadding = 15; // @designerBoardLeftRightPadding

	@tracked topOffset = 0;
	@tracked innerWidth = 0;

	// only set in construct and valid until first positioning update
	initialWidthStyle: SafeString | string = '';

	el!: JQuery;
	mainElement!: HTMLElement | null;

	_resizeFn: ResizeFn;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updatePositioning(true);
		this.initialWidthStyle = htmlSafe(`width:${this.outerWidth}px`);

		const resizeFn: ResizeFn = (e) => {
			const target = e.target;
			if (target && !$(target).hasClass('ui-resizable')) {
				// ignore events bubbled from resizable blocks
				debounce(this.updatePositioning, false, 100);
			}
		};
		this._resizeFn = resizeFn;

		$(window).on('resize.datawallcenter', this._resizeFn);
	}

	get outerWidth(): number {
		let w = this.innerWidth;
		if (w) {
			w += 2 * this.boardPadding;
		}
		return w;
	}

	get innerHeight(): number {
		// this is not an exact number - datawall footer (cca 6 em)
		// but we add extra reserve in updateInnerWidth
		return (this.innerWidth * 9) / 16;
	}

	get outerHeight(): number {
		return this.boardSettingsHeight + this.innerHeight + this.boardPadding;
	}

	get templateClass(): string {
		return this.args.board.isAccountTemplate ? 'is-template' : '';
	}

	get isLeftSidebarOpen(): boolean {
		return this.args.designerController.isLeftSidebarOpen;
	}

	get isRightSidebarOpen(): boolean {
		return this.args.designerController.isRightSidebarOpen;
	}

	get sourcesData() {
		return this.args.board.sourcesObjs;
	}

	updateInnerWidth(isInitialCheck: boolean): void {
		const ct = $('#main');

		if (!ct.length) {
			return;
		}

		const screenWidth = ct.width()!;

		// Calculate used width by sidebars, minimum margins, and board padding
		let usedWidth =
			this.leftSidebarTogglerWidth +
			this.rightSidebarOverflow +
			this.minMargin * 2 +
			this.boardPadding * 2;

		if (this.isLeftSidebarOpen) {
			usedWidth += this.openLeftSidebarWidth;
		}
		if (this.isRightSidebarOpen) {
			usedWidth += this.openRightSidebarWidth;
		}

		// Calculate free width available for the board
		const freeWidth = Math.max(0, screenWidth - usedWidth);

		// Calculate width based on available height to maintain 16:9 aspect ratio
		const availableHeight =
			$(window).height()! -
			this.topbarHeight -
			this.boardSettingsHeight -
			this.loopBoxHeight -
			this.minMargin * 2; // minimal top and bottom margins

		const widthForAvailableHeight = (availableHeight * 16) / 9;

		// Use the smaller of freeWidth or widthForAvailableHeight to maintain aspect ratio
		const width = Math.min(freeWidth, widthForAvailableHeight);

		if (isAlive(this) && (isInitialCheck || width !== this.innerWidth)) {
			this.innerWidth = width;

			if (this.el) {
				this.el.get(0)!.style.width = `${this.outerWidth}px`;
			}
		}
	}

	updateTopOffset(): void {
		// Set minimal topOffset to minMargin
		let topOffset = this.minMargin;

		const availableHeight =
			$(window).height()! - this.topbarHeight - this.outerHeight - this.loopBoxHeight;

		// If there's extra space, center vertically, but never go below minMargin
		if (availableHeight > this.minMargin * 2) {
			// minMargin on top and bottom
			topOffset = Math.max(this.minMargin, availableHeight / 2);
		}

		this.topOffset = topOffset;
		if (this.el) {
			this.el.get(0)!.style.top = `${topOffset}px`;
		}
	}

	positionDesignerPreview(): void {
		if (!this.el) {
			return;
		}

		// Calculate used width by sidebars, minimum margins, and board borders
		let usedWidth =
			this.leftSidebarTogglerWidth +
			this.rightSidebarOverflow +
			this.minMargin * 2 +
			this.boardPadding * 2;
		if (this.isLeftSidebarOpen) {
			usedWidth += this.openLeftSidebarWidth;
		}
		if (this.isRightSidebarOpen) {
			usedWidth += this.openRightSidebarWidth;
		}

		const freeWidth = Math.max(0, ($('#main').width() || 0) - usedWidth);

		// Set minimal leftMargin to minMargin
		let leftMargin = this.minMargin + this.leftSidebarTogglerWidth; // minimal margin + left toggle

		if (this.isLeftSidebarOpen) {
			leftMargin += this.openLeftSidebarWidth;
		}

		// Check if board fits within available space with minimum margins
		const totalRequiredWidth = this.outerWidth + this.minMargin * 2; // board width + left margin + right margin

		if (totalRequiredWidth <= freeWidth) {
			// Board fits with minimum margins, center it horizontally
			leftMargin = (freeWidth - this.outerWidth) / 2 + this.leftSidebarTogglerWidth;
			if (this.isLeftSidebarOpen) {
				leftMargin += this.openLeftSidebarWidth;
			}
		} else {
			// Board is too wide, ensure it doesn't exceed right margin
			const maxLeftMargin = freeWidth - this.outerWidth - this.minMargin;
			leftMargin = Math.max(this.minMargin + this.leftSidebarTogglerWidth, maxLeftMargin);

			if (this.isLeftSidebarOpen) {
				leftMargin += this.openLeftSidebarWidth;
			}
		}

		const boardInnerEl = this.el.find('.view-board-inner');

		const event = new CustomEvent('updatedPreviewPosition', {
			detail: {
				width: this.outerWidth,
				marginLeft: leftMargin,
			},
		});

		const boardInnerNode = boardInnerEl.get(0)!;
		boardInnerNode.dispatchEvent(event);

		this.el.get(0)!.style.marginLeft = `${leftMargin}px`;

		later(() => {
			boardInnerEl.trigger('custom.afterAnimate');
			boardInnerNode
				.querySelector('.bc__chart')
				?.dispatchEvent(new CustomEvent('blockPositionChanged'));
		}, 400);
	}

	@action
	scheduleUpdatePositioning(): void {
		debounce(this.updatePositioning, false, 100);
	}

	@action
	updatePositioning(isInitialCheck = false): void {
		if (!isAlive(this)) {
			return;
		}

		this.updateInnerWidth(isInitialCheck);
		this.updateTopOffset();

		scheduleOnce('afterRender', this, this.positionDesignerPreview);
	}

	@action
	setupView(node: HTMLElement): void {
		this.el = $(node);
		node.style.width = `${this.outerWidth}px`;
		node.style.top = `${this.topOffset}px`;

		this.mainElement = document.getElementById('main');
		this.mainElement?.addEventListener('mousedown', this.checkToCloseSidebars);
	}

	@action
	teardownView(): void {
		$(window).off('resize.datawallcenter', this._resizeFn);
		this.mainElement?.removeEventListener('mousedown', this.checkToCloseSidebars);
	}

	@action
	checkToCloseSidebars(e: MouseEvent): void {
		const target = e.target as HTMLElement;
		const mainElement = this.mainElement;

		if (mainElement && target === mainElement) {
			later(this, this.closeSidebars, 200);
		}
	}

	private closeSidebars() {
		const designerController = this.args.designerController;
		if (designerController) {
			designerController.closeRightSidebar();
			designerController.closeLeftSidebar();
		}
	}

	<template>
		<div
			id="designer-board-ct--wall"
			class={{this.templateClass}}
			style={{this.initialWidthStyle}}
			{{didInsert this.setupView}}
			{{didUpdate
				this.scheduleUpdatePositioning
				this.isLeftSidebarOpen
				this.isRightSidebarOpen
			}}
			{{willDestroy this.teardownView}}
		>

			<LayoutDesignerBoardSettingsComponent
				@model={{@board}}
				@isWallView={{true}}
				@width={{this.outerWidth}}
				@onBoardUpdate={{@onSave}}
				@onBoardDelete={{@onDelete}}
				@onBoardCopy={{@onCopy}}
				@onLoopChange={{@onLoopChange}}
				@designerController={{@designerController}}
			/>

			<BoardWallComponent
				@boardState={{@boardState}}
				@isInDesigner={{true}}
				@onSave={{@onSave}}
				@onDelete={{@onDelete}}
				@onCopy={{@onCopy}}
				@onDatawallGridChange={{@onDatawallGridChange}}
				@trackingOrigin="designer"
				@boardSources={{this.sourcesData}}
				@isLeftSidebarOpen={{this.isLeftSidebarOpen}}
				@isRightSidebarOpen={{this.isRightSidebarOpen}}
			/>

		</div>
	</template>
}
